import React from "react";

/**
 * VoiceResultPreview - Shows extracted data from voice note for confirmation
 */
export default function VoiceResultPreview({
  result,
  onConfirm,
  onEdit,
  onCancel,
}) {
  if (!result) return null;

  const { transcript, extracted, confidence, needs_review } = result;

  const confidencePercent = Math.round((confidence || 0) * 100);

  return (
    <div
      style={{
        background: "white",
        borderRadius: "16px",
        boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "16px",
          borderBottom: "1px solid #e2e8f0",
          background: "#f8fafc",
        }}
      >
        <h3
          style={{
            margin: 0,
            fontSize: "16px",
            fontWeight: "600",
            color: "#1e293b",
          }}
        >
          Voice Note Captured
        </h3>
        <span
          style={{
            padding: "4px 10px",
            borderRadius: "12px",
            fontSize: "12px",
            fontWeight: "600",
            background: needs_review ? "#fef3c7" : "#dcfce7",
            color: needs_review ? "#92400e" : "#166534",
          }}
        >
          {needs_review ? "Needs Review" : "Auto-verified"}
        </span>
      </div>

      <div style={{ padding: "16px" }}>
        {/* Transcript */}
        <div style={{ marginBottom: "16px" }}>
          <label
            style={{
              display: "block",
              fontSize: "11px",
              fontWeight: "600",
              color: "#64748b",
              textTransform: "uppercase",
              marginBottom: "6px",
            }}
          >
            What you said
          </label>
          <p
            style={{
              margin: 0,
              padding: "12px",
              background: "#f8fafc",
              borderRadius: "10px",
              border: "1px solid #e2e8f0",
              fontSize: "14px",
              color: "#475569",
              fontStyle: "italic",
            }}
          >
            "{transcript || "No transcript available"}"
          </p>
        </div>

        {/* Extracted Data */}
        <div style={{ marginBottom: "16px" }}>
          <label
            style={{
              display: "block",
              fontSize: "11px",
              fontWeight: "600",
              color: "#64748b",
              textTransform: "uppercase",
              marginBottom: "6px",
            }}
          >
            What we captured
          </label>
          <div
            style={{
              padding: "12px",
              background: "#f0f9ff",
              borderRadius: "10px",
              border: "1px solid #bae6fd",
            }}
          >
            <ExtractedDataDisplay data={extracted} />
          </div>
        </div>

        {/* Confidence indicator */}
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <span
            style={{
              fontSize: "11px",
              fontWeight: "600",
              color: "#64748b",
              textTransform: "uppercase",
            }}
          >
            Confidence
          </span>
          <div
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <div
              style={{
                flex: 1,
                background: "#e2e8f0",
                borderRadius: "4px",
                height: "8px",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  height: "100%",
                  borderRadius: "4px",
                  background:
                    confidence > 0.8
                      ? "#22c55e"
                      : confidence > 0.5
                        ? "#eab308"
                        : "#ef4444",
                  width: `${confidencePercent}%`,
                  transition: "width 0.3s",
                }}
              />
            </div>
            <span
              style={{
                fontSize: "14px",
                fontWeight: "600",
                color: "#475569",
                minWidth: "40px",
              }}
            >
              {confidencePercent}%
            </span>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div
        style={{
          display: "flex",
          gap: "10px",
          padding: "16px",
          borderTop: "1px solid #e2e8f0",
          background: "#f8fafc",
        }}
      >
        <button
          onClick={onCancel}
          style={{
            flex: 1,
            padding: "12px 16px",
            borderRadius: "10px",
            border: "1px solid #e2e8f0",
            background: "white",
            fontSize: "14px",
            fontWeight: "600",
            color: "#64748b",
            cursor: "pointer",
          }}
        >
          Cancel
        </button>

        <button
          onClick={onConfirm}
          style={{
            flex: 2,
            padding: "12px 16px",
            borderRadius: "10px",
            border: "none",
            background: "#1a5a8e",
            fontSize: "14px",
            fontWeight: "600",
            color: "white",
            cursor: "pointer",
          }}
        >
          {needs_review ? "Confirm" : "Apply"}
        </button>
      </div>
    </div>
  );
}

/**
 * ExtractedDataDisplay - Renders extracted data based on its structure
 */
function ExtractedDataDisplay({ data }) {
  if (!data || Object.keys(data).length === 0) {
    return (
      <p
        style={{
          margin: 0,
          color: "#94a3b8",
          fontSize: "14px",
          fontStyle: "italic",
        }}
      >
        No data extracted
      </p>
    );
  }

  // Filter out confidence/needs_review from display
  const displayData = { ...data };
  delete displayData.confidence;
  delete displayData.needs_review;

  // Shift note with various fields
  const items = [];

  if (displayData.area) {
    items.push({ label: "Area", value: displayData.area });
  }
  if (displayData.activity) {
    items.push({ label: "Activity", value: displayData.activity });
  }
  if (displayData.quantities && displayData.quantities.length > 0) {
    const qtyText = displayData.quantities
      .map((q) => `${q.qty} ${q.item}`)
      .join(", ");
    items.push({ label: "Quantities", value: qtyText });
  }
  if (displayData.materials && displayData.materials.length > 0) {
    items.push({ label: "Materials", value: displayData.materials.join(", ") });
  }
  if (displayData.issues && displayData.issues.length > 0) {
    items.push({
      label: "Issues",
      value: displayData.issues.join(", "),
      isIssue: true,
    });
  }
  if (displayData.notes) {
    items.push({ label: "Notes", value: displayData.notes });
  }

  // Time entries for clockout recap
  if (displayData.time_entries && displayData.time_entries.length > 0) {
    const entries = displayData.time_entries
      .map(
        (e) =>
          `${e.employee_name}: ${e.start_time || "?"} - ${e.end_time || "?"}`,
      )
      .join("; ");
    items.push({ label: "Time Entries", value: entries });
  }

  // Material request items
  if (displayData.items && displayData.items.length > 0) {
    const itemList = displayData.items
      .map((i) => `${i.qty} ${i.unit || ""} ${i.item}`)
      .join(", ");
    items.push({ label: "Items", value: itemList });
  }

  if (items.length === 0) {
    // Fallback to JSON
    return (
      <pre
        style={{
          margin: 0,
          fontSize: "12px",
          color: "#475569",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        {JSON.stringify(displayData, null, 2)}
      </pre>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
      {items.map((item, i) => (
        <div key={i} style={{ fontSize: "14px" }}>
          <span style={{ fontWeight: "600", color: "#334155" }}>
            {item.label}:
          </span>{" "}
          <span style={{ color: item.isIssue ? "#ea580c" : "#475569" }}>
            {item.value}
          </span>
        </div>
      ))}
    </div>
  );
}
